import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Award, Users, Target, Sparkles } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Pet-First Approach',
      description: 'Every product is designed with your pet\'s safety, comfort, and happiness as our top priority.'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'We rigorously test all products to ensure they meet the highest safety and durability standards.'
    },
    {
      icon: Award,
      title: 'Premium Materials',
      description: 'Only non-toxic, pet-safe materials are used in our toys. No compromises, ever.'
    },
    {
      icon: Users,
      title: 'Customer Care',
      description: 'Our dedicated support team is always here to help you find the perfect toys for your pets.'
    }
  ];

  const stats = [
    { value: '50K+', label: 'Happy Customers' },
    { value: '200+', label: 'Products' },
    { value: '4.9', label: 'Average Rating' },
    { value: '30', label: 'Day Returns' }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#FFF9F5] to-[#FFE8D4] py-16 md:py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block bg-brand-orange-light text-brand-orange font-medium px-4 py-2 rounded-full text-sm mb-4">
              Our Story
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About NexaFusion
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              We believe every pet deserves toys that bring joy, promote health, and create lasting bonds with their owners. Our mission is to provide premium, safe, and engaging pet toys that make tails wag and purrs louder.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Founded with a simple yet powerful vision: to create toys that bring out the best in every pet. At NexaFusion, we understand that pets aren't just animals – they're family members who deserve the very best.
                </p>
                <p>
                  Our team of pet lovers and product designers work tirelessly to develop toys that are not only fun but also safe, durable, and beneficial for your pet's physical and mental well-being.
                </p>
                <p>
                  From interactive puzzle toys that stimulate the mind to durable chew toys that promote dental health, every NexaFusion product is crafted with love and purpose.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?w=600&h=500&fit=crop"
                alt="Happy pets playing"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-brand-orange text-white p-6 rounded-2xl">
                <p className="font-heading font-bold text-3xl">5+ Years</p>
                <p className="text-sm opacity-90">Serving Pet Parents</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#FFF9F5]">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at NexaFusion
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-3xl text-center hover:shadow-xl transition-shadow duration-300"
                data-testid={`value-${idx}`}
              >
                <div className="w-16 h-16 bg-brand-orange-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-brand-orange" />
                </div>
                <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-brand-orange text-white">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="font-heading text-4xl md:text-5xl font-bold mb-2">{stat.value}</p>
                <p className="opacity-90">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <Sparkles className="w-12 h-12 text-brand-orange mx-auto mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Promise to You
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              When you shop at NexaFusion, you're not just buying a toy – you're investing in your pet's happiness. We promise to deliver only the highest quality products, backed by our 30-day satisfaction guarantee. If you or your pet aren't completely satisfied, we'll make it right.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-brand-orange-light text-brand-orange px-4 py-2 rounded-full">
                <Target className="w-5 h-5" />
                <span className="font-medium">100% Satisfaction Guaranteed</span>
              </div>
              <div className="flex items-center gap-2 bg-brand-blue-light text-brand-blue px-4 py-2 rounded-full">
                <Shield className="w-5 h-5" />
                <span className="font-medium">Pet-Safe Materials</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
